import { useMemo, useState } from 'react'
import { ActionIcon, Badge, Collapse, Group, Paper, ScrollArea, Stack, Text } from '@mantine/core'
import { IconChevronDown, IconChevronRight, IconShoppingCart, IconX } from '@tabler/icons-react'

export interface CartItem {
  id: string
  code: string
  name: string
  category: string
  subItems?: { code: string; name: string }[]
}

type OrderCartProps = {
  items: CartItem[]
  onRemove: (id: string) => void
  vendorLabel?: string
}

export const OrderCart = ({ items, onRemove, vendorLabel }: OrderCartProps) => {
  const grouped = useMemo(() => {
    const map: Record<string, CartItem[]> = {}
    for (const item of items) {
      if (!map[item.category]) map[item.category] = []
      map[item.category].push(item)
    }
    return map
  }, [items])

  // Track which panels have collapsed sub-items (default: all expanded)
  const [collapsedPanels, setCollapsedPanels] = useState<Set<string>>(new Set())
  const togglePanel = (id: string) =>
    setCollapsedPanels((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <Paper p="md" withBorder radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <IconShoppingCart size={18} className="text-gray-500" />
          <Text fw={600} size="sm">
            {vendorLabel ? `${vendorLabel} Order` : 'Order Cart'}
          </Text>
        </Group>
        {items.length > 0 && (
          <Badge size="sm" variant="filled" color="blue">
            {items.length}
          </Badge>
        )}
      </Group>

      {items.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          No tests selected
        </Text>
      ) : (
        <ScrollArea.Autosize mah="calc(100vh - 220px)" offsetScrollbars="y">
          <Stack gap="md">
            {Object.entries(grouped).map(([category, categoryItems]) => (
              <div key={category}>
                <Text size="xs" fw={600} c="dimmed" mb={4}>
                  {category} ({categoryItems.length})
                </Text>
                <Stack gap={4}>
                  {categoryItems.map((item) => (
                    <div key={item.id}>
                      <Group justify="space-between" wrap="nowrap" gap="xs">
                        {item.subItems && item.subItems.length > 0 ? (
                          <Group
                            gap={4}
                            wrap="nowrap"
                            style={{ flex: 1, cursor: 'pointer' }}
                            onClick={() => togglePanel(item.id)}
                          >
                            {collapsedPanels.has(item.id) ? (
                              <IconChevronRight size={12} className="shrink-0 text-gray-400" />
                            ) : (
                              <IconChevronDown size={12} className="shrink-0 text-gray-400" />
                            )}
                            <Text size="xs" fw={500} lineClamp={1}>
                              {item.name}
                            </Text>
                            <Badge size="xs" variant="light" color="gray">
                              {item.subItems.length}
                            </Badge>
                          </Group>
                        ) : (
                          <Text size="xs" lineClamp={1} style={{ flex: 1 }}>
                            {item.name}
                          </Text>
                        )}
                        <ActionIcon
                          variant="subtle"
                          size="xs"
                          color="gray"
                          onClick={() => onRemove(item.id)}
                        >
                          <IconX size={12} />
                        </ActionIcon>
                      </Group>
                      {item.subItems && item.subItems.length > 0 && (
                        <Collapse in={!collapsedPanels.has(item.id)}>
                          <Stack gap={1} ml="md" mt={2} mb={4}>
                            {item.subItems.map((sub) => (
                              <Text key={sub.code} size="xs" c="dimmed">
                                {sub.name}
                              </Text>
                            ))}
                          </Stack>
                        </Collapse>
                      )}
                    </div>
                  ))}
                </Stack>
              </div>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      )}
    </Paper>
  )
}
